export * from "ce/selectors/workflowSelectors";
import type { DefaultRootState } from "react-redux";
import { selectFeatureFlags } from "ee/selectors/featureFlagsSelectors";

import { createSelector } from "reselect";
import type { ActionDataState } from "ee/reducers/entityReducers/actionsReducer";
import type { JSCollectionDataState } from "ee/reducers/entityReducers/jsActionsReducer";

export const getIsFetchingWorkflows = (state: DefaultRootState) =>
  state.ui.workspaces.loadingStates.isFetchingWorkflowsList;

export const getIsCreatingWorkflow = (
  state: DefaultRootState,
  workspaceId: string,
) => state.ui.workspaces.loadingStates.workflowCreationRequestMap[workspaceId];

export const getWorkflowFromWorkspaceById = (
  state: DefaultRootState,
  workflowId: string,
) => state.ui.workspaces.workflowsList.find((w) => w.id === workflowId);

export const getWorkflowsList = (state: DefaultRootState) =>
  state.ui.workspaces.workflowsList;

export const getCurrentWorkflowId = (state: DefaultRootState) =>
  state.ui.editor?.currentWorkflowId;

export const getWorkflows = (state: DefaultRootState) =>
  state.entities.workflows;

export const getCurrentWorkflowById =
  (workflowId: string) => (state: DefaultRootState) => {
    return getWorkflows(state)[workflowId];
  };

export const getCurrentWorkflow = createSelector(
  getCurrentWorkflowId,
  getWorkflows,
  (currentWorkflowId, workflows) =>
    currentWorkflowId ? workflows[currentWorkflowId] : null,
);
export const getCurrentWorkflowName = createSelector(
  getCurrentWorkflowId,
  getWorkflows,
  (currentWorkflowId, workflows) =>
    currentWorkflowId ? workflows[currentWorkflowId].name : "",
);

export const getIsWorkflowEditorInitialized = (state: DefaultRootState) =>
  state.ui.editor.isWorkflowEditorInitialized;

export const getIsSavingWorkflowName = (state: DefaultRootState) =>
  state.ui.workspaces.isSavingWorkflowName;

export const getisErrorSavingWorkflowName = (state: DefaultRootState) =>
  state.ui.workspaces.isErrorSavingWorkflowName;

export const getMainJsObjectIdOfCurrentWorkflow = (state: DefaultRootState) => {
  const workflow = getCurrentWorkflow(state);

  // return workflow.mainJsObjectId if workflow exists
  return workflow?.mainJsObjectId || "";
};

/**
 * Checks if the release_workflows_enabled feature flag is enabled and if the
 * current instance is cloud hosted. This is the base condition for enabling
 * workflows.
 *
 * @returns boolean
 */
export const getShowWorkflowFeature = createSelector(
  selectFeatureFlags,
  (featureFlags) => {
    return featureFlags.release_workflows_enabled;
  },
);

export const getIsCurrentEditorWorkflowType = createSelector(
  getCurrentWorkflowId,
  (currentWorkflowId) => !!currentWorkflowId,
);

// TODO: Remove this selector and use the one from selectors/workflowSelectors.ts instead
// Did this to avoid error due to cyclic dependency
const getActions = (state: DefaultRootState): ActionDataState =>
  state.entities.actions;

const getJSCollections = (state: DefaultRootState): JSCollectionDataState =>
  state.entities.jsActions;

export const getCurrentWorkflowActions = createSelector(
  getCurrentWorkflowId,
  getActions,
  (workflowId, actions) => {
    if (!actions || actions.length === 0) return [];

    if (!workflowId) return [];

    return actions.filter((a) => a.config.workflowId === workflowId);
  },
);

export const getCurrentWorkflowJSActions = createSelector(
  getCurrentWorkflowId,
  getJSCollections,
  (workflowId, jsActions) => {
    if (!jsActions || jsActions.length === 0) return [];

    if (!workflowId) return [];

    return jsActions.filter((a) => a.config.workflowId === workflowId);
  },
);

export const getIsWorkflowPublishing = (state: DefaultRootState) =>
  state.ui.editor.isWorkflowPublishing;

export const getIsWorkflowTokenGenerating = (state: DefaultRootState) =>
  state.ui.editor.isWorkflowTokenGenerating;

export const getIsWorkflowScheduleSaving = (state: DefaultRootState) =>
  state.ui.editor.isWorkflowScheduleSaving;

export const getIsWorkflowTokenDetails = createSelector(
  getCurrentWorkflow,
  (workflow) => {
    return {
      token: workflow?.token || "",
      tokenGenerated: workflow?.tokenGenerated || false,
    };
  },
);

export const getWorkflowTestTriggerState = (state: DefaultRootState) =>
  state.ui.workflowHistoryPane.testRunContext.isTriggering;

export const getWorkflowTestRunId = (state: DefaultRootState) =>
  state.ui.workflowHistoryPane.testRunContext.testRunId;

export const getWorkflowSchedules = createSelector(
  getCurrentWorkflow,
  (workflow) => {
    return {
      schedules: workflow?.schedules,
    };
  },
);

export const getWorkflowTabIds = createSelector(
  getCurrentWorkflowId,
  (state: DefaultRootState) => state.ui.workflowIDE.workflowTabIds,
  (workflowId, workflowTabIds) => {
    return (workflowId && workflowTabIds[workflowId]) || new Set();
  },
);

export const getWorkflowTabs = createSelector(
  getWorkflowTabIds,
  getCurrentWorkflowActions,
  (tabIds, actions) =>
    Array.from(tabIds)
      .map((id) => actions.find((a) => a.config.id === id))
      .filter((tab) => !!tab),
);

export const getActionNameSavingStatuses = (state: DefaultRootState) =>
  state.ui.workflowIDE.actionNameSavingStatus;
